import React, { useState, useEffect } from "react";
import { Avatar, PriorityBadge, PRIORITY } from "./common";
import { fmtTime, fileUrl } from "../lib/api";
import { FileText, Trash2, Pencil, Megaphone, CheckCheck, Download } from "lucide-react";

const DECRYPT_GLYPHS = "ABCDEF0123456789/#$%&*<>[]{}@!?¥§Ø+=~";
const decryptedIds = new Set();
function reducedMotion() {
  return typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function DecryptText({ text, onDone }) {
  const [disp, setDisp] = useState("");
  useEffect(() => {
    if (!text) { onDone?.(); return; }
    if (reducedMotion()) { setDisp(text); onDone?.(); return; }
    const dur = Math.min(1700, 520 + text.length * 22);
    let raf, start;
    const step = (ts) => {
      if (!start) start = ts;
      const p = Math.min(1, (ts - start) / dur);
      const rev = Math.floor(p * text.length);
      let out = "";
      for (let i = 0; i < text.length; i++) {
        const ch = text[i];
        out += (i < rev || ch === " " || ch === "\n") ? ch : DECRYPT_GLYPHS[Math.floor(Math.random() * DECRYPT_GLYPHS.length)];
      }
      setDisp(out);
      if (p < 1) raf = requestAnimationFrame(step);
      else { setDisp(text); onDone?.(); }
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [text]); // eslint-disable-line
  return <span>{disp}</span>;
}

function Attachment({ att }) {
  if (!att) return null;
  const isImg = (att.content_type || "").startsWith("image/");
  if (isImg) {
    return (
      <a href={fileUrl(att.path)} target="_blank" rel="noreferrer" className="block mt-2 rounded-xl overflow-hidden border border-[color:var(--r-primary)]/25">
        <img src={fileUrl(att.path)} alt={att.filename} className="max-w-full max-h-72 hover:brightness-105 transition-all" style={{ objectFit: "cover", display: "block" }} />
      </a>
    );
  }
  return (
    <a href={fileUrl(att.path)} target="_blank" rel="noreferrer" className="group/file flex items-center gap-2.5 mt-2 px-3 py-2.5 rounded-lg border border-[color:var(--r-primary)]/25 hover:border-[color:var(--r-primary)] transition-colors" style={{ background: "color-mix(in srgb, var(--r-void) 55%, transparent)" }}>
      <FileText size={18} style={{ color: "var(--r-primary)" }} />
      <div className="min-w-0 flex-1">
        <div className="font-mono text-xs truncate" style={{ color: "var(--r-text)" }}>{att.filename}</div>
        <div className="sys-label">FICHIER CHIFFRÉ</div>
      </div>
      <Download size={15} className="opacity-50 group-hover/file:opacity-100 transition-opacity" style={{ color: "var(--r-primary)" }} />
    </a>
  );
}

export function MessageBubble({ msg, showSender = true, grouped = false, decrypt = false, gmControls = false, onEdit, onDelete }) {
  const own = msg.own;
  const align = own ? "items-end" : "items-start";
  const bubbleBg = own ? "color-mix(in srgb, var(--r-primary) 16%, var(--r-surface))" : "var(--r-surface)";
  const borderCol = own ? "color-mix(in srgb, var(--r-primary) 45%, transparent)" : "color-mix(in srgb, var(--r-text-muted) 22%, transparent)";
  const radius = own
    ? (grouped ? "rounded-2xl rounded-tr-md rounded-br-md" : "rounded-2xl rounded-tr-sm")
    : (grouped ? "rounded-2xl rounded-tl-md rounded-bl-md" : "rounded-2xl rounded-tl-sm");
  const [decrypting, setDecrypting] = useState(() => decrypt && !msg.deleted && !!msg.content && !decryptedIds.has(msg.id));
  useEffect(() => { if (decrypt && msg.id) decryptedIds.add(msg.id); }, []); // eslint-disable-line

  return (
    <div className={`flex flex-col ${align} ${grouped ? "mt-0.5" : "mt-4"} pop-in group`} data-testid={`message-${msg.id}`}>
      <div className={`flex gap-2 max-w-[88%] sm:max-w-[78%] ${own ? "flex-row-reverse" : ""}`}>
        {!own && (grouped ? <span style={{ width: 34, minWidth: 34 }} aria-hidden /> : <Avatar src={msg.sender_avatar} name={msg.sender_name} size={34} />)}
        <div className="min-w-0">
          {showSender && !own && !grouped && (
            <div className="flex items-baseline gap-2 mb-1">
              <span className="font-semibold text-sm" style={{ color: "var(--r-text)" }}>{msg.sender_name}</span>
              {msg.sender_subtitle && <span className="sys-label">{msg.sender_subtitle}</span>}
            </div>
          )}
          <div
            className={`px-3.5 py-2.5 relative overflow-hidden ${msg.deleted ? "rounded-2xl" : radius} ${decrypting ? "decrypting" : ""}`}
            style={{
              background: msg.deleted ? "transparent" : bubbleBg,
              border: `1px solid ${msg.deleted ? "color-mix(in srgb, var(--r-text-muted) 22%, transparent)" : borderCol}`,
              boxShadow: msg.deleted ? "none" : (own ? "0 2px 10px color-mix(in srgb, var(--r-primary) 8%, transparent)" : "0 2px 8px rgba(0,0,0,0.22)"),
            }}
          >
            {decrypting && (
              <div className="font-mono text-[9px] tracking-[0.22em] mb-1 flex items-center gap-1.5" style={{ color: own ? "var(--r-primary)" : "var(--r-secondary)" }} data-testid={`decrypt-label-${msg.id}`}>
                {own ? "⤤ CHIFFREMENT" : "⟲ DÉCRYPTAGE"}<span className="blink">●</span>
              </div>
            )}
            {msg.priority && msg.priority !== "NORMAL" && (
              <div className="mb-1.5"><PriorityBadge level={msg.priority} /></div>
            )}
            <div
              className="text-sm whitespace-pre-wrap break-words leading-relaxed"
              style={{ color: msg.deleted ? "var(--r-text-muted)" : "var(--r-text)", fontStyle: msg.deleted ? "italic" : "normal", fontFamily: decrypting ? "var(--r-mono)" : undefined }}
            >
              {decrypting ? <DecryptText text={msg.content} onDone={() => setDecrypting(false)} /> : msg.content}
            </div>
            {!msg.deleted && !decrypting && <Attachment att={msg.attachment} />}
            {decrypting && <span className="decrypt-sweep" aria-hidden />}
          </div>
          <div className={`flex items-center gap-2 mt-1 px-1 ${own ? "justify-end" : ""}`}>
            <span className="ts text-[color:var(--r-text-muted)]">{fmtTime(msg.created_at)}</span>
            {msg.edited_at && <span className="sys-label">MODIFIÉ</span>}
            {own && !msg.deleted && (
              <span className="tick" title="Transmission chiffrée · livrée"><CheckCheck size={13} style={{ color: "var(--r-primary)" }} /></span>
            )}
            {gmControls && !msg.deleted && (
              <span className="hidden group-hover:flex items-center gap-1">
                <button data-testid={`edit-message-${msg.id}`} onClick={() => onEdit?.(msg)} className="text-[color:var(--r-text-muted)] hover:text-[color:var(--r-primary)]"><Pencil size={12} /></button>
                <button data-testid={`delete-message-${msg.id}`} onClick={() => onDelete?.(msg)} className="text-[color:var(--r-text-muted)] hover:text-[color:var(--r-alert)]"><Trash2 size={12} /></button>
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export function AdItem({ msg, gmControls = false, onDelete }) {
  const isImg = msg.attachment && (msg.attachment.content_type || "").startsWith("image/");
  return (
    <div className="fade-up mb-3" data-testid={`ad-item-${msg.id}`}>
      <div className="relative overflow-hidden rounded-lg" style={{ border: "1px solid color-mix(in srgb, var(--r-secondary) 55%, transparent)", background: "color-mix(in srgb, var(--r-secondary) 8%, var(--r-surface))", boxShadow: "0 0 0 1px color-mix(in srgb, var(--r-secondary) 12%, transparent)" }}>
        <div className="flex items-center justify-between px-3 py-1.5" style={{ borderBottom: "1px solid color-mix(in srgb, var(--r-secondary) 28%, transparent)" }}>
          <span className="font-mono text-[9px] tracking-[0.25em] flex items-center gap-1.5" style={{ color: "var(--r-secondary)" }}>
            <Megaphone size={11} /> PUBLICITÉ
          </span>
          <div className="flex items-center gap-2">
            <span className="sys-label" style={{ textTransform: "none", letterSpacing: "normal" }}>{msg.sender_name}</span>
            {gmControls && !msg.deleted && (
              <button data-testid={`delete-message-${msg.id}`} onClick={() => onDelete?.(msg)} className="text-[color:var(--r-text-muted)] hover:text-[color:var(--r-alert)]"><Trash2 size={12} /></button>
            )}
          </div>
        </div>
        {isImg && !msg.deleted && (
          <a href={fileUrl(msg.attachment.path)} target="_blank" rel="noreferrer" className="block">
            <img src={fileUrl(msg.attachment.path)} alt={msg.attachment.filename} className="w-full max-h-72" style={{ objectFit: "cover" }} />
          </a>
        )}
        <div className="p-3">
          {msg.priority && msg.priority !== "NORMAL" && <div className="mb-1.5"><PriorityBadge level={msg.priority} /></div>}
          <div className="text-sm whitespace-pre-wrap break-words" style={{ color: msg.deleted ? "var(--r-text-muted)" : "var(--r-text)", fontStyle: msg.deleted ? "italic" : "normal" }}>
            {msg.content}
          </div>
          <div className="ts mt-2 text-[color:var(--r-text-muted)]">{fmtTime(msg.created_at)}</div>
        </div>
      </div>
    </div>
  );
}

export function FeedItem({ msg, gmControls = false, onDelete }) {
  const p = PRIORITY[msg.priority] || PRIORITY.NORMAL;
  const urgent = ["ALERTE", "URGENT", "FLASH"].includes(msg.priority);
  return (
    <div
      className="fade-up mb-3 relative overflow-hidden rounded-lg"
      data-testid={`feed-item-${msg.id}`}
      style={{ background: "color-mix(in srgb, var(--r-surface) 92%, transparent)", border: `1px solid color-mix(in srgb, ${p.color} 30%, transparent)`, boxShadow: urgent ? `0 0 16px color-mix(in srgb, ${p.color} 18%, transparent)` : "none" }}
    >
      <span style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 3, background: p.color, boxShadow: `0 0 10px ${p.color}` }} />
      <div className="pl-4 pr-3 py-2.5">
        <div className="flex items-center gap-3">
          <PriorityBadge level={msg.priority} />
          <span className="sys-label" style={{ textTransform: "none", letterSpacing: "normal" }}>{msg.sender_name}</span>
          <span className="ts ml-auto text-[color:var(--r-text-muted)]">{fmtTime(msg.created_at)}</span>
          {gmControls && !msg.deleted && (
            <button data-testid={`delete-message-${msg.id}`} onClick={() => onDelete?.(msg)} className="text-[color:var(--r-text-muted)] hover:text-[color:var(--r-alert)]"><Trash2 size={12} /></button>
          )}
        </div>
        <div className="text-sm mt-2 whitespace-pre-wrap break-words" style={{ color: msg.deleted ? "var(--r-text-muted)" : "var(--r-text)", fontStyle: msg.deleted ? "italic" : "normal" }}>
          {msg.content}
        </div>
      </div>
    </div>
  );
}
